import type { OperationVariables, TypedDocumentNode } from "@apollo/client";
import type { Currency, SortOrder } from "@src/__generated__/graphql";
import type { BodyColumnDef } from "@src/components/ui-kit";
import { FetchHelper } from "@src/helpers/apollo/fetch";
import { type Filter, Filters } from "@src/utils/components/filters/models";
import { OrderBy } from "@src/utils/components/sorting/OrderBy";
import { PaginationState } from "@src/utils/mobx/states/PaginationState";
import { action, computed, makeObservable, observable } from "mobx";

export type MatchPaymentTableItem = {
  id: string;
  amount: string;
  documentNo: string;
  currency: Currency;
  exchange_rate?: number | null;
};
export class MatchPaymentTableState<
  Query,
  Variables extends OperationVariables,
  WhereColumn,
  OrderByColumn,
  TableItem extends MatchPaymentTableItem,
> {
  fetcher: FetchHelper<Query, Variables>;
  columns;

  @observable.ref selectedIds: string[] = [];
  @observable.ref tableData: TableItem[] = [];
  @observable selectedTableItems: MatchPaymentTableItem[] = [];

  @observable where;
  @observable pagination;
  @observable.ref orderBy: OrderBy<OrderByColumn>;
  @observable.ref searchTerm = "";

  constructor(
    public readonly tableKey: string,
    queryDocument: TypedDocumentNode<Query, Variables>,
    filterInitArgs: Filter<WhereColumn>[],
    initOrderByClause: { order: SortOrder; column: OrderByColumn }[],
    columns: BodyColumnDef<TableItem, OrderByColumn>[],
    private onChangeFilters: () => void,
    private onSelectRow: (
      selectedIds: string[],
      selectedTableItems: MatchPaymentTableItem[],
    ) => void,
  ) {
    makeObservable(this);
    this.fetcher = new FetchHelper(queryDocument);
    this.columns = columns;

    this.where = new Filters(filterInitArgs, {
      onChange: () => {
        this.reset();
        onChangeFilters();
      },
    });
    this.orderBy = new OrderBy(initOrderByClause);
    this.pagination = new PaginationState(`${tableKey}-pagination`, {
      onChangePagination: onChangeFilters,
    });
  }

  @computed get fetchVariables(): Variables {
    return {
      first: this.pagination.asParams.first,
      page: this.pagination.asParams.page,
      filters: {
        search: this.searchTerm,
        where: this.where.asWhereParam,
        orderBy: this.orderBy.asWhereParam,
      },
    } as unknown as Variables;
  }

  @computed get tableProps() {
    return {
      data: this.tableData,
      tableKey: this.tableKey,
      columns: this.columns,
      selected: this.selectedIds,
      onSelect: this.handleSelectRow,
      orderedBy: this.orderBy.clauses[0],
      onOrderByChange: (clause: {
        order: SortOrder | undefined;
        column: OrderByColumn;
      }) => {
        if (!clause.order) {
          this.orderBy.setClauses([]);
          this.onChangeFilters();
          return;
        }
        this.orderBy.setClauses([
          { column: clause.column, order: clause.order },
        ]);
        this.onChangeFilters();
      },
      pagination: this.pagination,
      isLoading: this.fetcher.isLoading.value,
      filters: this.where,
      onSearch: this.handleSearch,
      searchTerm: this.searchTerm,
    };
  }

  @action.bound handleSearch(newSearchTerm: string) {
    this.searchTerm = newSearchTerm;
    this.onChangeFilters();
  }

  @action.bound reset(
    resetFilters?: boolean,
    columnsToExclude?: WhereColumn[],
    skipOnChange: boolean = true,
    keepSelectedIds: boolean = true,
  ) {
    if (!keepSelectedIds) {
      this.selectedIds = [];
    }
    this.searchTerm = "";
    this.tableData = [];
    this.selectedTableItems = [];
    this.pagination.resetPage();
    if (!resetFilters) return;
    this.where.reset(columnsToExclude, skipOnChange);
  }

  @action setTableData(newData: TableItem[]) {
    this.tableData = newData;
  }

  @action.bound handleSelectRow(ids: string[]) {
    this.selectedIds = ids;

    this.setSelectedTableItems(
      this.selectedTableItems.filter(({ id }) => ids.includes(id)),
    );

    for (const id of ids) {
      if (this.selectedTableItems.find((item) => item.id === id)) continue;

      const matchingItem = this.tableData.find((item) => item.id === id);
      if (!matchingItem) continue;
      this.selectedTableItems.push(matchingItem);
    }

    this.onSelectRow(ids, this.selectedTableItems);
  }

  @action.bound setSelectedTableItems(tableItems: MatchPaymentTableItem[]) {
    this.selectedTableItems = tableItems;
  }
}
